import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import theme from './DocumentUpload.module.scss';
import { addResidentialUnitDocument, pdfToImage, addProjectDocument, UploadedDocumentVersion } from '@/services/documents';
import Dropzone from 'react-dropzone';
import { uuidv4 } from '@/utils/uuid';
import { FileData, FileType } from '@ygtechnologies/rubus-frontend-utilities';
import {
    Checkbox,
    Crop,
    cropImage,
    isValidCrop,
    LoadingIndicator,
    LocalFilePreview,
    LocalImageCrop,
    Toast,
    LoadingOverlay,
} from '@ygtechnologies/bygr-frontend-component-library';
import { rotateImage } from '@ygtechnologies/rubus-frontend-utilities';
import { ValidationScope, ValidationSummary, ValidatingInput } from '@/components/forms/validation';
import { Button } from '@/components/Buttons';
import { DocumentTypeSelectGrouped } from './DocumentTypeSelect';
import { connect, useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { useValidationManager, useMergeState } from '@/utils/hooks';
import { DocumentType } from '@/types';
import SensitiveInfoCheckbox from '@/components/SensitiveInfoCheckbox';

function getFileType(document) {
    if (document.fileType) {
        return document.fileType;
    }

    if (document.fileData) {
        return document.fileData.getFileType();
    }

    return FileType.Unknown;
}

const mapStateToProps = (state: IState) => ({ documentTypes: state.seller.documentTypes });

type IOwnProps = ReturnType<typeof mapStateToProps> & {
    // show: boolean;
    disableDrop?: boolean;
    document: any;
    projectId: number;
    residentialUnitId?: number;
    onClose?: () => void;
    onSave?: (document: any) => void;
    onBack?: () => void;
    closeOnUpload?: boolean;
};

function getNewDocument(profileId: number): UploadedDocumentVersion {
    return {
        name: '',
        documentId: uuidv4(),
        floor: 1,
        documentTypeId: null,
        hideFromBuyer: false,
        isSensitive: false,
        fileData: null,
        croppedImageFileData: null,
        ownerProfileId: profileId,
    };
}

const DocumentUpload: React.FC<IOwnProps> = ({
    document: initialDocument,
    disableDrop,
    residentialUnitId,
    projectId,
    onClose,
    onSave,
    // show,
    documentTypes,
    closeOnUpload = true,

    onBack,
}) => {
    const [crop, setCrop] = useState<Crop | null>();
    const [saving, setSaving] = useState<boolean>();
    const [document, mergeDocument, setDocument] = useMergeState<UploadedDocumentVersion | null>(null);
    const [pdfImage, setPdfImage] = useState<FileData | null>();
    const imageRef = useRef<HTMLImageElement>();
    const profileId = useSelector((state: IState) => state.global.activeProfile!.id);

    // state: IOwnState = { ...initialState };

    const validationManager = useValidationManager([
        {
            isValid: () => !!document && !!document.fileData,
            message: () => <Trans>You must upload a file</Trans>,
            summaryMessage: () => <Trans>You must upload a file</Trans>,
        },
    ]);

    const resetState = useCallback(() => {
        setCrop(null);
        setSaving(false);
        setPdfImage(null);
        setDocument(null);
    }, [setDocument]);

    useEffect(() => {
        function doConvertIfNeeded() {
            if (document && getFileType(document) === FileType.Pdf) {
                pdfToImage(document.fileData!).then((x) => {
                    setPdfImage(x);
                });
            }
        }

        if (document && document.fileData) {
            doConvertIfNeeded();
        }
    }, [document]);

    useEffect(() => {
        resetState();
        setDocument({ ...initialDocument, ownerProfileId: profileId });
    }, [resetState, initialDocument, setDocument, profileId]);

    const isCroppingAllowed = () => {
        if (!document || !document.documentTypeId) {
            return false;
        }

        const documentType = documentTypes!.find((x) => x.id === +document.documentTypeId!);

        if (!documentType) {
            return false;
        }

        return documentType.isCroppable;
    };

    // componentDidUpdate(prevProps: IOwnProps, prevState: IOwnState) {
    //     const { document } = this.state;

    //     if (document && (document !== prevState.document || document.fileData !== prevState.document.fileData)) {
    //         this.doConvertIfNeeded();
    //     }
    // }

    const handleSaveClick = async (addAnother) => {
        var errors = validationManager.validate();

        if (errors.length > 0) {
            return;
        }

        if (!document) {
            return;
        }

        setSaving(true);

        if (crop && isCroppingAllowed() && isValidCrop(crop)) {
            const croppedImage = await cropImage(pdfImage || document.fileData!, crop, imageRef.current);

            document.croppedImageFileData = new FileData(croppedImage);
        }
        try {
            if (residentialUnitId) {
                await addResidentialUnitDocument(residentialUnitId, document);
            } else {
                await addProjectDocument(projectId, document);
            }
            triggerHandler(onSave, document);

            if (addAnother) {
                resetState();
                setDocument(getNewDocument(profileId));
            } else {
                if (closeOnUpload) {
                    triggerHandler(onClose);
                }
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleCropChange = (crop) => {
        setCrop(crop);
    };

    const handleCropImageLoad = (image: HTMLImageElement) => {
        imageRef.current = image;
    };

    const handleDrop = async (files: File[]) => {
        if (disableDrop) {
            return;
        }

        const newDocument = { ...document! };

        const file = files[0];

        if (file.size === 0) {
            Toast.error(<Trans>Cannot drag and drop files directly from compressed folders (.ZIP)</Trans>);
            return;
        }

        newDocument.fileData = new FileData(file);

        if (!newDocument.name) {
            newDocument.name = newDocument.fileData!.getFileNameWithoutExtension();
        }

        if (newDocument.fileData!.getFileType() === FileType.Image) {
            newDocument.fileData = await rotateImage(newDocument.fileData!);
        }

        setDocument(newDocument);
        setPdfImage(null);
    };

    const renderDocumentPreview = () => {
        const wrap = (children, disableClick = false) => (
            <Dropzone onDrop={handleDrop} noClick={disableClick}>
                {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()} className={theme.uploadPlaceholder}>
                        <input {...getInputProps()} />
                        {children}
                    </div>
                )}
            </Dropzone>
        );

        if (!document || !document.fileData) {
            return wrap(<Trans>Drag files or click here to upload</Trans>);
        }

        const cropProps = {
            crop: crop!,
            onChange: handleCropChange,
            onImageLoaded: handleCropImageLoad,
        };

        let fileData = document.fileData;

        if (getFileType(document) === FileType.Pdf) {
            if (!pdfImage) {
                return wrap(<LoadingIndicator />);
            }

            fileData = pdfImage;
        }

        return isCroppingAllowed()
            ? wrap(
                  <div className={theme.preview}>
                      <LocalImageCrop
                          fileData={fileData}
                          {...cropProps}
                          //   style={{ height: 'auto' }}
                          style={{ maxHeight: '100%', height: 'auto' }}
                          imageStyle={{ maxHeight: '50vh', height: 'auto' }}
                      />
                  </div>,
                  true
              )
            : wrap(
                  <div className={theme.preview}>
                      <LocalFilePreview fileData={fileData} />
                  </div>
              );
    };

    const documentTypeFilter = useCallback(
        (type: DocumentType) => {
            if (!type.permissions.canUploadDocuments) {
                return false;
            }

            if (residentialUnitId) {
                return type.allowedOnResidentialUnit;
            }

            return type.allowedOnProject;
        },
        [residentialUnitId]
    );

    const renderBody = () => {
        if (!document) {
            return null;
        }

        return (
            <>
                <div className={theme.info}>
                    <div className="form-group">
                        <label>
                            <Trans>Name</Trans>
                        </label>
                        <ValidatingInput
                            type="text"
                            className="form-control"
                            placeholder={t`Name`}
                            name="name"
                            onChange={(e) => mergeDocument({ name: e.target.value })}
                            value={document.name || ''}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>
                            <Trans id="document_type">Type</Trans>
                        </label>

                        <DocumentTypeSelectGrouped
                            placeholder={t`Please select...`}
                            fieldName={t`Agreement type`}
                            filter={documentTypeFilter}
                            value={document.documentTypeId || ''}
                            onChange={(id) => mergeDocument({ documentTypeId: id })}
                            required
                        />
                    </div>
                </div>

                {renderDocumentPreview()}
                {isCroppingAllowed() && (
                    <p>
                        <Trans>Move and adjust the size of the dotted square in order to adjust the image</Trans>
                    </p>
                )}

                <div className={theme.checkboxContainer}>
                    <Checkbox
                        checked={document.hideFromBuyer}
                        onChange={(e) => mergeDocument({ hideFromBuyer: e.target.checked })}
                        labelClassName={theme.checkbox}>
                        <Trans>Hide document from buyer</Trans>
                    </Checkbox>
                </div>

                <div className={theme.checkboxContainer}>
                    <SensitiveInfoCheckbox
                        ownerProfileId={document.ownerProfileId}
                        checked={document.isSensitive || false}
                        onChange={(flag) => mergeDocument({ isSensitive: flag })}>
                        <Trans>Contains sensitive data</Trans>
                    </SensitiveInfoCheckbox>
                </div>

                <ValidationSummary />
            </>
        );
    };

    if (!document) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <div className={theme.container}>
                <ValidationScope validationManager={validationManager}>{renderBody()}</ValidationScope>
                {saving ? <LoadingOverlay /> : null}

                <div className="flex">
                    <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                        <Trans>Cancel</Trans>
                    </Button>

                    <div className="flex end">
                        {onBack && (
                            <Button color="tertiary" onClick={onBack} disabled={saving}>
                                <Trans>Back</Trans>
                            </Button>
                        )}

                        {/* <Button color="secondary" onClick={() => this.handleSaveClick(true)} disabled={!document || saving}>
                        <Trans>Upload And Add Another</Trans>
                    </Button> */}

                        <Button color="primary" onClick={() => handleSaveClick(false)} disabled={!document || saving}>
                            <Trans>Upload</Trans>
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default connect(mapStateToProps)(DocumentUpload);
